import React from 'react';

/**
 * ConfirmModal 컴포넌트
 *
 * 재사용 가능한 확인/알림 모달
 * - 경고, 성공, 에러, 정보 등 다양한 타입 지원
 * - 버튼 커스터마이징 가능
 */

export type ModalType = 'warning' | 'success' | 'error' | 'info' | 'danger';

interface ConfirmModalButton {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'outline';
  disabled?: boolean;
  isLoading?: boolean;
}

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  type?: ModalType;
  title: string;
  message: string | React.ReactNode;
  infoBox?: {
    content: string | React.ReactNode;
  };
  buttons?: ConfirmModalButton[];
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  type = 'info',
  title,
  message,
  infoBox,
  buttons = [{ label: '확인', onClick: onClose, variant: 'primary' }],
}) => {
  if (!isOpen) return null;

  // 타입별 아이콘 및 색상
  const typeConfig = {
    warning: {
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      iconPath:
        'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
      infoBg: 'bg-blue-50',
      infoBorder: 'border-blue-200',
      infoText: 'text-blue-800',
    },
    success: {
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      infoBg: 'bg-green-50',
      infoBorder: 'border-green-200',
      infoText: 'text-green-800',
    },
    error: {
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      iconPath: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
      infoBg: 'bg-red-50',
      infoBorder: 'border-red-200',
      infoText: 'text-red-800',
    },
    info: {
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      iconPath: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      infoBg: 'bg-blue-50',
      infoBorder: 'border-blue-200',
      infoText: 'text-blue-800',
    },
    danger: {
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      iconPath:
        'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
      infoBg: 'bg-red-50',
      infoBorder: 'border-red-200',
      infoText: 'text-red-800',
    },
  };

  const config = typeConfig[type];

  // 버튼 스타일 variant별
  const getButtonClass = (variant: string = 'primary', disabled: boolean = false) => {
    const baseClass = 'px-5 py-2.5 text-base rounded-lg transition-colors font-medium flex items-center gap-2';
    const disabledClass = disabled ? 'opacity-50 cursor-not-allowed' : '';

    const variantClasses = {
      primary: 'bg-indigo-500 text-white hover:bg-indigo-600',
      secondary: 'bg-slate-200 text-slate-700 hover:bg-slate-300',
      danger: 'bg-red-500 text-white hover:bg-red-600',
      outline: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50',
    };

    return `${baseClass} ${variantClasses[variant as keyof typeof variantClasses]} ${disabledClass}`;
  };

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black">
      <div className="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start gap-4">
          {/* 아이콘 */}
          <div className={`h-12 w-12 flex-shrink-0 ${config.iconBg} flex items-center justify-center rounded-full`}>
            <svg className={`h-6 w-6 ${config.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={config.iconPath} />
            </svg>
          </div>

          {/* 내용 */}
          <div className="flex-1">
            <h3 className="mb-3 text-2xl font-semibold text-slate-800">{title}</h3>
            <div className="mb-3 text-base text-slate-600">{message}</div>

            {/* 정보 박스 (선택적) */}
            {infoBox && (
              <div className={`${config.infoBg} border ${config.infoBorder} mt-4 rounded-lg p-4`}>
                <div className={`text-sm ${config.infoText} leading-relaxed`}>{infoBox.content}</div>
              </div>
            )}
          </div>
        </div>

        {/* 버튼들 */}
        <div className="mt-6 flex justify-end gap-3">
          {buttons.map((button, index) => (
            <button
              key={index}
              onClick={button.onClick}
              disabled={button.disabled || button.isLoading}
              className={getButtonClass(button.variant, button.disabled || button.isLoading)}>
              {button.isLoading ? (
                <>
                  <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  <span>처리 중...</span>
                </>
              ) : (
                button.label
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
